import { QueryKey } from '@tanstack/react-query';
import { ICurrency } from 'api/account/business/dtos';
import { Membership } from 'api/account/membership/dtos';
import { CurrencyName, Permission } from 'api/shared';
import { IStudent } from 'api/student/dtos';
import dayjs from 'dayjs';
import { GenderEnum } from '../api/employee/dtos';
import { Operation } from './permissions/operations';
import { Resource } from './permissions/resources';

export function isDev() {
  const env = import.meta.env.VITE_ENVIRONMENT || 'dev';

  return env === 'dev' || env === 'qa';
}

export function roundToTwoDecimal(number: number) {
  return Math.round((number + Number.EPSILON) * 100) / 100;
}

export function displayPrice(
  amount?: number | string,
  currency?: CurrencyName,
  currencies?: ICurrency[],
) {
  const parsedAmount = roundToTwoDecimal(Number(amount || 0));
  const parsedCurrency = (currency ?? getDefault(currencies)?.name ?? '').toUpperCase();

  const formatter = new Intl.NumberFormat('en-US');

  return formatter.format(parsedAmount) + (parsedCurrency ? ' ' + parsedCurrency : '');
}

export function displayDefaultPrice(amount?: number | string, currencies?: ICurrency[]) {
  return displayPrice(amount, undefined, currencies);
}

export function getDisplayName(data?: { firstName?: string; lastName?: string }) {
  if (data && data.firstName && data.lastName) {
    return `${data.firstName} ${data.lastName}`;
  }

  return '';
}

export function getDisplayGender(gender?: GenderEnum) {
  if (!gender) return;

  if (gender === GenderEnum.MALE) return 'Male';
  return 'Female';
}

export function getDisplayBirth(dateOfBirth: string) {
  if (dateOfBirth == null) return;
  return `${dateOfBirth} (${dayjs().diff(dayjs(dateOfBirth), 'years')})`;
}

export function getStudentExtraInfo(student: IStudent, whitespace = false) {
  if (!student) return;

  let output = '';
  if (student.gender) output += `${student.gender.toUpperCase()} `;
  if (student.dateOfBirth && dayjs(student.dateOfBirth).isValid())
    output += `(${dayjs().diff(dayjs(student.dateOfBirth), 'y')})`;

  if (output === '') return undefined;

  if (whitespace) output = ' ' + output;

  return output;
}

export function isEmpty(value: any) {
  if (value == null) return true;
  if (value === '') return true;
  if (typeof value === 'string' && !value.replace(/\s/g, '').length) return true;
  if (typeof value === 'object' && !Object.keys(value).length) return true;
}

export function findItemById<T>(
  data: (T & { id: number })[] = [],
  id?: number,
): (T & { id: number }) | undefined {
  return data?.find((e) => e.id === id);
}

export function getDefault<T>(
  data?: (T & { isDefault: boolean })[],
): (T & { isDefault: boolean }) | undefined {
  return data?.find((e) => e.isDefault);
}

export function getDefaultCurrency(currencies: ICurrency[]) {
  return getDefault(currencies)?.name ?? 'n/a';
}

// duration in minutes
export function getDisplayTime(duration: number, humanize = false) {
  if (duration === 0) return '0h';
  let delta = duration;
  let weeks = 0;
  let days = 0;

  if (humanize) {
    weeks = Math.floor(delta / 60 / 24 / 7);
    delta -= weeks * 60 * 24 * 7;

    days = Math.floor(delta / 60 / 24);
    delta -= days * 60 * 24;
  }

  const hours = Math.floor(delta / 60);
  delta -= hours * 60;

  const minutes = Math.floor(delta);
  let output = '';

  const isEmpty = (v: string) => (v === '' ? '' : ' ');

  if (weeks > 0) output += isEmpty(output) + `${weeks}w`;
  if (days > 0) output += isEmpty(output) + `${days}d`;
  if (hours > 0) output += isEmpty(output) + `${hours}h`;
  if (minutes > 0) output += isEmpty(output) + `${minutes}m`;

  return output;
}

export function generateId() {
  return Math.random().toString(36).substr(2, 8);
}

export function strToRGB(string: string) {
  function hashCode(str: string) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  }

  function intToRGB(i: number) {
    const c = (i & 0x00ffffff).toString(16).toUpperCase();
    return 'ffffff'.substring(0, 6 - c.length) + c;
  }

  return `#${intToRGB(hashCode(string))}`;
}

export function exchangeCurrency(
  currencies: ICurrency[],
  from: CurrencyName,
  to: CurrencyName,
  amount: number,
) {
  if (currencies == null || currencies.length === 0) return 0;
  const ratio_from = currencies.find((c) => c?.name === from)?.ratio || 0;
  const ratio_to = currencies.find((c) => c?.name === to)?.ratio || 0;
  if (ratio_from === 0 || ratio_to === 0) return 0;

  const ratio = ratio_from / ratio_to;

  return roundToTwoDecimal(amount * ratio);
}

export function getDefaultCurrencyAmount(
  currencies: ICurrency[],
  currency: string | undefined,
  amount: number,
) {
  const defaultCurrency = getDefault(currencies);
  if (!defaultCurrency) return 0;

  return exchangeCurrency(
    currencies,
    currency ?? defaultCurrency.name,
    defaultCurrency.name,
    amount,
  );
}

export { default as logger } from './logger';

export function pickBy<T extends object>(data?: T, fn?: (entry: T[keyof T]) => boolean) {
  const obj: Partial<T> = {};
  if (!data) return obj;

  for (const key in data) {
    if (fn ? fn(data[key]) : data[key] != null) {
      obj[key] = data[key];
    }
  }

  return obj;
}

export const getSeasonEndDateOrToday = (seasonEndDate?: string) => {
  if (!seasonEndDate) return;

  return dayjs().isBefore(seasonEndDate) ? dayjs().endOf('d').toISOString() : seasonEndDate;
};

export function extractReactQueryKey(key: QueryKey | string) {
  if (typeof key === 'string') return [key];

  if (Array.isArray(key)) return key.reduce<unknown[]>((acc, value) => acc.concat(value), []);
  return [];
}

export interface CompressedMembership {
  s: number;
  [key: number]: number[];
}

export interface CompressedMemberships {
  [key: number]: CompressedMembership;
}

export const decompressMemberships = (tokenPermissions: CompressedMemberships) => {
  const resources = Object.values(Resource);
  const operations = Object.values(Operation);

  const copyPermisisons: any = { ...tokenPermissions };
  delete copyPermisisons.v;
  const memberships: Record<string, Membership> = {};
  Object.entries(copyPermisisons).forEach(([businessId, compressedMembership]: any) => {
    const membership: any = {};
    if (compressedMembership.s != null) {
      if (compressedMembership.s > 0) {
        membership.validTo = dayjs.unix(compressedMembership.s);
      } else {
        membership.validTo = dayjs().add(1, 'month'); // FIXME remove once all the schools are on subscription
      }
      delete compressedMembership.s;
    }
    const permissions: Permission[] = [];
    Object.entries(compressedMembership).forEach(([scopeIndex, operationIndexes]) => {
      (operationIndexes as any).forEach((operationIndex: string) => {
        const scope = resources[parseInt(scopeIndex)];
        const operation = operations[parseInt(operationIndex)];
        permissions.push({ scope, operation });
      });
    });
    membership.permissions = permissions;
    memberships[businessId] = membership;
  });

  return memberships;
};
